import { Controller } from "@hotwired/stimulus"

// Cart-side episode picker for the movie show page.
//
// Tracks which episodes the user selected across Turbo-paginated pages of
// the episode list (selection survives pagination), keeps the counters in
// sync, and injects the full selected_episodes set into the cart form on
// submit (the form's natural payload only sees the currently-rendered page).
//
// Replaces the inline <script> block in movies/materials/_list.html.erb.
export default class extends Controller {
  static targets = ["markAll", "counter", "counterLabel", "submitButton"]
  static values  = {
    movieId:       Number,
    selectedLabel: { type: String, default: "Selected" }
  }

  connect() {
    this.selectedEpisodes = new Set()
    this.updateUI()
  }

  toggleAll(event) {
    const checked = event.target.checked
    this.episodeCheckboxes().forEach(cb => {
      if (cb.checked !== checked) {
        cb.checked = checked
        this.applyCheckboxState(cb)
      }
    })
    this.updateUI()
  }

  // Bubbled change events from any input inside the controller scope.
  onCheckboxChange(event) {
    const cb = event.target
    if (cb.name !== "episode_ids[]") return
    this.applyCheckboxState(cb)
    this.updateUI()
  }

  // Re-check checkboxes for episodes the user selected on previous pages.
  // Fires after Turbo replaces the <turbo-frame id="episodes"> contents.
  onFrameRender() {
    this.episodeCheckboxes().forEach(cb => {
      if (this.selectedEpisodes.has(cb.value)) cb.checked = true
    })
    this.updateUI()
  }

  // Replace the cart form's payload with the full selection (across pages).
  injectSelected(event) {
    const params = new URLSearchParams()
    params.append("movie_id", String(this.movieIdValue))
    for (const id of this.selectedEpisodes) {
      params.append("episode_ids[]", id)
    }
    event.detail.formSubmission.fetchRequest.body = params

    if (this.hasSubmitButtonTarget) this.submitButtonTarget.disabled = true
  }

  // Re-enable the submit button when the request finished without redirecting
  // (success case redirects to /lk/cart and Turbo navigates away — the button
  // and the whole page leave the DOM). Without this the user stays stuck with
  // a disabled button if the cart endpoint returned a validation error.
  onSubmitEnd(event) {
    if (event.detail.fetchResponse?.redirected) return
    if (this.hasSubmitButtonTarget && this.selectedEpisodes.size > 0) {
      this.submitButtonTarget.disabled = false
    }
  }

  // -- private helpers --

  applyCheckboxState(cb) {
    if (cb.checked) this.selectedEpisodes.add(cb.value)
    else            this.selectedEpisodes.delete(cb.value)
  }

  updateUI() {
    const count = this.selectedEpisodes.size
    if (this.hasCounterTarget)      this.counterTarget.textContent      = String(count)
    if (this.hasCounterLabelTarget) this.counterLabelTarget.textContent = `${this.selectedLabelValue} ${count}`
    if (this.hasSubmitButtonTarget) this.submitButtonTarget.disabled    = count === 0
  }

  episodeCheckboxes() {
    return this.element.querySelectorAll('input[name="episode_ids[]"]')
  }
}
